<template>
  <div v-if="!result" class="calculation-steps default">
    <h2 class="h4">
      Step-by-Step Confidence Interval Calculation
    </h2>
    <p class="bottom-margins">
      We need to perform the following steps to estimate a confidence interval:
    </p>
    <ol>
      <li>
        <div>
          <p>Calculate the sample’s mean value:</p>
          <p>\[\bar{X}=\frac{\sum_{i=1}^{n}X_i}{n}\]</p>
        </div>
      </li>
      <li>
        <div class="top-margins">
          <p>Determine the sample’s standard deviation:</p>
          <p>\[s=\sqrt{\sum_{i=1}^{n}\frac{(X_i-\bar{X})^2}{n-1}}\]</p>
        </div>
        <p class="bottom-margins">
          Alternatively, use the population standard deviation when it’s known.
        </p>
      </li>
      <li>
        <div class="top-margins">
          <p>
            Compute the sample mean’s <nuxt-link to="/tutorials/statistics-tutorials/central-limit-theorem/" target="_blank">
              standard error
            </nuxt-link> using the following formula when the population variance is:
          </p>
          <ul>
            <li>
              <p class="small-margins">
                <span class="bolded-texts">known:</span>
              </p>
              <p>\[\sigma_{\bar{X}}=\frac{\sigma}{\sqrt n}\]</p>
            </li>
            <li>
              <p>
                <span class="bolded-texts">unknown:</span>
              </p>
              <p>\[s_{\bar{X}}=\frac{s}{\sqrt n}\]</p>
            </li>
          </ul>
        </div>
      </li>
      <li>
        <p class="top-margins bottom-margins">
          Choose the
          <a
            data-section="heading3"
            @click="scrollToSection"
          >confidence level</a>.<br>
          Common confidence levels are 90%, 95%, and 99% (with α=10%, α=5%, and α=1%, respectively).
        </p>
      </li>
      <li class="bottom-margins">
        <p>Estimate the level of significance:</p>
        <p>\[\alpha=1-Confidence\ Level\]</p>
      </li>
      <li>
        <p class="top-margins">
          Obtain the z-score from the standard normal distribution table when the population variance is <span class="bolded-texts">known</span> for probability (p) = 1 - α/For instance, the z-score for a 95% confidence interval is 1.9You can find the standard normal distribution critical values and the corresponding (1-a) in the <nuxt-link to="/calculators/resources/snd-table/" target="_blank">
            z-table
          </nuxt-link>.
        </p>
        <p class="small-margins">
          Alternatively, calculate the t-statistic from the <nuxt-link to="/calculators/resources/std-table/" target="_blank">
            Student’s t table
          </nuxt-link> when the population variance is <span class="bolded-texts">unknown</span> for probability (p) = 1 - α/2 and n-1 degrees of freedom.
        </p>
        <p class="small-margins">
          Estimate the margin of error by multiplying the standard error by the z-score (when the population variance is <span class="bolded-texts">known</span>) or the t-statistic (when the population variance is <span class="bolded-texts">unknown</span>):
        </p>
        <p>\[z_{\alpha/2}\frac{\sigma}{\sqrt n}\]</p>
        <p class="bottom-margins">
          \[t_{\left(n-1\right),\alpha/2}\frac{s}{\sqrt n}\]
        </p>
      </li>
      <li>
        <div>
          <p class="top-margins">
            Calculate the confidence interval by adding and subtracting the margin of error from the mean value:
          </p>
          <ul>
            <li>
              <p class="small-margins">
                When the population variance is <span class="bolded-texts">known:</span>
              </p>
              <p>\[\bar{X}\pm\ z_{\alpha/2}\frac{\sigma}{\sqrt n}\]</p>
            </li>
            <li>
              <p>When the population variance is <span class="bolded-texts">unknown:</span></p>
              <p>\[\bar{X}\pm\ t_{\left(n-1\right),\alpha/2}\frac{s}{\sqrt n}\]</p>
            </li>
          </ul>
        </div>
      </li>
    </ol>

    <div v-if="$store.state.layout === 'desktop'" class="top-margins">
      <p>
        Confidence intervals provide us with an estimation of where the parameters are located. However, when making a decision, you need a Yes-or-No answer. The correct approach, in this case, is to use <nuxt-link to="/tutorials/statistics-tutorials/null-hypothesis/" target="_blank">
          hypothesis testing
        </nuxt-link>, which you can perform using the Hypothesis Testing Calculator.
      </p>
      <p class="small-margins">
        Moreover, the Confidence Interval Calculator can be only used for one population mean. When you need to explore intervals by looking into two populations, you can use the Confidence Interval (Two Populations) Calculator.
      </p>
    </div>
    <div v-else class="top-margins">
      <p>
        Confidence intervals provide us with an estimation of where the parameters are located. However, when you are making a decision, you need a Yes/No answer. The correct approach in this case is to use hypothesis testing. Hypothesis testing can be done using the Hypothesis Testing Calculator. The confidence interval calculator can be only used for one population mean. When you need to explore confidence intervals by looking into two populations you can use the Confidence Interval (Two Populations) Calculator.
      </p>
    </div>
  </div>

  <div v-else :key="result" class="calculation-steps result">
    <h2 class="h4">
      Confidence Interval Calculation Results
    </h2>
    <p class="bottom-margins">
      In order to estimate a confidence interval, we need to perform the following steps:
    </p>
    <ol>
      <li>
        <div
          v-if="calculationData.method === 'rawData'"
        >
          <p>Calculate the sample’s mean value:</p>
          <p class="formulas">
            \[\bar{X}=\frac{\sum_{i=1}^{n}X_i}{n}=\frac{ {{ additionString }} }{ {{ calculationData.size }} }={{ calculationData.mean }}\]
          </p>
        </div>
        <div
          v-if="calculationData.method === 'readyData'
            && calculationData.answer === 'No'"
        >
          <p>Compute the standard error of the sample mean’s standard error:</p>
          <p class="formulas">
            \[s_{\bar{X}}=\frac{s}{\sqrt n}=\frac{ {{ calculationData.standardDeviation }} }{\sqrt{ {{ calculationData.size }} }}={{ calculationData.standardError }}\]
          </p>
        </div>
        <div
          v-if="calculationData.method === 'readyData'
            && calculationData.answer === 'Yes'"
        >
          <p>Compute the standard error of the sample mean:</p>
          <p class="formulas">
            \[\sigma_{\bar{X}}=\frac{\sigma}{\sqrt n}=\frac{ {{ calculationData.standardDeviation }} }{\sqrt{ {{ calculationData.size }} }}={{ calculationData.standardError }}\]
          </p>
        </div>
      </li>
      <li>
        <div
          v-if="calculationData.method === 'rawData'
            && calculationData.answer === 'Yes'"
          class="top-margins"
        >
          <p>Compute the sample mean’s standard error:</p>
          <p class="formulas">
            \[s_{\bar{X}}=\frac{s}{\sqrt n}=\frac{ {{ calculationData.mean }} }{\sqrt{ {{ sizeMinusOne }} }}={{ calculationData.standardError }}\]
          </p>
        </div>
        <div
          v-if="calculationData.method === 'rawData'
            && calculationData.answer === 'No'"
          class="top-margins"
        >
          <p>Determine the sample’s standard deviation:</p>
          <p class="formulas">
            \[s=\sqrt{\sum_{i=1}^{n}{\frac{(X_i-\bar{X})^2}{n-1}=}}\sum_{i=1}^{n}{\frac{ {{ sqrString }} }{ {{ calculationData.size }}-1}= {{ calculationData.standardDeviation }} }\]
          </p>
        </div>
        <div
          v-if="calculationData.method === 'readyData'"
          class="top-margins"
        >
          <p>Estimate the level of significance:</p>
          <p class="formulas">
            \[\alpha=1-Confidence\ Level=1-{{ calculationData.confidenceLevel }}={{ calculationData.levelOfSignificance }}\]
          </p>
        </div>
      </li>
      <li>
        <div
          v-if="calculationData.method === 'rawData'
            && calculationData.answer === 'No'"
          class="top-margins"
        >
          <p>Compute the sample mean’s standard error:</p>
          <p class="formulas">
            \[s_{\bar{X}}=\frac{s}{\sqrt n}=\frac{ {{ calculationData.mean }} }{\sqrt{ {{ sizeMinusOne }} }}={{ calculationData.standardError }}\]
          </p>
        </div>
        <div
          v-if="calculationData.method === 'readyData'
            && calculationData.answer === 'No'"
          class="top-margins"
        >
          <p>Obtain the t-statistic from the Student’s t table  for probability (p) = 1 – {{ calculationData.levelOfSignificance }}/2 and {{ calculationData.size }}-1 degrees of freedom:</p>
          <p class="formulas">
            \[t_{ {{ sizeMinusOne }},{{ losDividedByTwo }}}={{ calculationData.currentScore }}\]
          </p>
        </div>
        <div
          v-if="calculationData.method === 'rawData'
            && calculationData.answer === 'Yes'"
          class="top-margins"
        >
          <p>Estimate the level of significance:</p>
          <p class="formulas">
            \[\alpha=1-Confidence\ Level=1-{{ calculationData.confidenceLevel }}={{ calculationData.levelOfSignificance }}\]
          </p>
        </div>
        <div
          v-if="calculationData.method === 'readyData'
            && calculationData.answer === 'Yes'"
          class="top-margins"
        >
          <p>Obtain the z-score from the standard normal distribution table for probability (p) = 1 – {{ calculationData.levelOfSignificance }}/2:</p>
          <p class="formulas">
            \[z_{ {{ losDividedByTwo }} }={{ calculationData.currentScore }}\]
          </p>
        </div>
      </li>
      <DynamicTables
        v-if="numberContained"
        :current-score="getCurrentTableAndScore[1]"
        :current-table="getCurrentTableAndScore[0]"
      />
      <li>
        <div
          v-if="calculationData.method === 'rawData'
            && calculationData.answer === 'No'"
          class="top-margins"
        >
          <p>Estimate the level of significance:</p>
          <p class="formulas">
            \[\alpha=1-Confidence\ Level=1-{{ calculationData.confidenceLevel }}={{ calculationData.levelOfSignificance }}\]
          </p>
        </div>
        <div
          v-if="calculationData.method === 'rawData'
            && calculationData.answer === 'Yes'"
          class="top-margins"
        >
          <p>Obtain the z-score from the standard normal distribution table for probability (p) = 1 – {{ calculationData.levelOfSignificance }}/2:</p>
          <p class="formulas">
            \[z_{ {{ losDividedByTwo }} }={{ calculationData.currentScore }}\]
          </p>
        </div>
        <div
          v-if="calculationData.method === 'readyData'
            && calculationData.answer === 'Yes'"
          class="top-margins"
        >
          <p>Estimate the <span class="bolded-texts">margin of error:</span></p>
          <p class="formulas">
            \[z_{\alpha/2}\frac{\sigma}{\sqrt n}={{ calculationData.currentScore }}\frac{ {{ calculationData.standardDeviation }} }{\sqrt{ {{ calculationData.size }} }}={{ calculationData.marginOfError }}\]
          </p>
        </div>
        <div
          v-if="calculationData.method === 'readyData'
            && calculationData.answer === 'No'"
          class="top-margins"
        >
          <p>Estimate the <span class="bolded-texts">margin of error:</span></p>
          <p class="formulas">
            \[t_{\left(n-1\right),\alpha/2}\frac{s}{\sqrt n}={{ calculationData.currentScore }}\frac{ {{ calculationData.standardDeviation }} }{\sqrt{ {{ calculationData.size }} }}={{ calculationData.marginOfError }}\]
          </p>
        </div>
      </li>
      <li>
        <div
          v-if="calculationData.method === 'rawData'
            && calculationData.answer === 'No'"
          class="top-margins"
        >
          <p>Obtain the t-statistic from the Student’s t table  for probability (p) = 1 – {{ calculationData.levelOfSignificance }}/2 and {{ calculationData.size }}-1 degrees of freedom:</p>
          <p class="formulas">
            \[t_{ {{ sizeMinusOne }},{{ losDividedByTwo }}}={{ calculationData.currentScore }}\]
          </p>
        </div>
        <div
          v-if="calculationData.method === 'readyData'"
          class="top-margins"
        >
          <p>Calculate the confidence interval by adding and subtracting the margin of error from the mean value:</p>
          <p class="formulas">
            \[{{ calculationData.mean }}\pm{{ calculationData.marginOfError }}\]
          </p>
          <p class="formulas">
            \[\ {{ meanMinusMof }}\ \ to\ {{ meanPlusMof }} \]
          </p>
        </div>
        <div
          v-if="calculationData.method === 'rawData'
            && calculationData.answer === 'Yes'"
          class="top-margins"
        >
          <p>Estimate the <span class="bolded-texts">margin of error:</span></p>
          <p class="formulas">
            \[t_{\left(n-1\right),\alpha/2}\frac{s}{\sqrt n}={{ calculationData.currentScore }}\frac{ {{ calculationData.standardDeviation }} }{\sqrt{ {{ sizeMinusOne }} }}={{ calculationData.marginOfError }}\]
          </p>
        </div>
      </li>
      <li
        v-if="
          calculationData.method === 'rawData' && calculationData.answer === 'No' || calculationData.method === 'rawData' && calculationData.answer === 'Yes'"
      >
        <div
          v-if="calculationData.method === 'rawData'
            && calculationData.answer === 'No'"
          class="top-margins"
        >
          <p>Estimate the <span class="bolded-texts">margin of error:</span></p>
          <p class="formulas">
            \[t_{\left(n-1\right),\alpha/2}\frac{s}{\sqrt n}={{ calculationData.currentScore }}\frac{ {{ calculationData.standardDeviation }} }{\sqrt{ {{ sizeMinusOne }} }}={{ calculationData.marginOfError }}\]
          </p>
        </div>
        <div
          v-if="calculationData.method === 'rawData'
            && calculationData.answer === 'Yes'"
          class="top-margins"
        >
          <p>Calculate the confidence interval by adding and subtracting the margin of error from the mean value:</p>
          <p class="formulas">
            \[{{ calculationData.mean }}\pm{{ calculationData.marginOfError }}\]
          </p>
          <p class="formulas">
            \[\ {{ meanMinusMof }}\ \ to\ {{ meanPlusMof }} \]
          </p>
        </div>
      </li>
      <li
        v-if="calculationData.method === 'rawData'
          && calculationData.answer === 'No'"
      >
        <div
          class="top-margins"
        >
          <p>Calculate the confidence interval by adding and subtracting the margin of error from the mean value:</p>
          <p class="formulas">
            \[{{ calculationData.mean }}\pm{{ calculationData.marginOfError }}\]
          </p>
          <p class="formulas">
            \[\ {{ meanMinusMof }}\ \ to\ {{ meanPlusMof }} \]
          </p>
        </div>
      </li>
    </ol>

    <div class="python-r-codes python">
      <span class="img-text">
        <img alt="Python icon" src="../../../../assets/images/icon-python-calc.png">
        <h4 class="h6">
          Your Result in Python:
        </h4>
      </span>
      <p class="codes-info">
        The following Python code should provide the confidence interval using the input data you entered.
      </p>
      <div class="codes-container">
        <div class="codes">
          <pre
            v-if="calculationData.method === 'rawData'
              && calculationData.answer === 'No'"
            class="language-python"
          ><code id="python-r-n">import numpy as np

from scipy.stats import t

# new data

data = <span class="no-wrap">{{ calculationData.rawDataMeanArrayActualInput }}</span>

# calculate the sample mean

mean = np.mean(data)

# calculate the sample standard deviation

std = np.std(data, ddof=1)

# specify the desired level of confidence

alpha = {{ calculationData.levelOfSignificance }}

# find the t-score for the given level of confidence

t_score = t.ppf(1 - alpha/2, len(data) - 1)

# calculate the standard error

se = std / np.sqrt(len(data))

# calculate the lower and upper bounds of the confidence interval

lower = mean - t_score * se

upper = mean + t_score * se

print(f'Confidence interval: [{lower:.4f}, {upper:.4f}]') </code></pre>

          <pre
            v-if="calculationData.method === 'readyData'
              && calculationData.answer === 'No'"
            class="language-python"
          ><code id="python-re-n">import numpy as np

from scipy.stats import t

# Sample Standard Deviation

s = {{ calculationData.standardDeviation }}

# Sample Mean

mean = {{ calculationData.mean }}

# Sample Size

n = {{ calculationData.size }}

# Confidence Level

alpha = {{ calculationData.levelOfSignificance }}

# Degrees of Freedom

df = n - 1

# Calculate t-critical value

t_critical = t.ppf(1 - alpha/2, n - 1)

# Calculate Margin of Error

moe = t_critical * (s / (n ** 0.5))

# Calculate Confidence Interval

ci = [mean - moe, mean + moe]

print(f'Confidence interval: [{ci[0]:.4f}, {ci[1]:.4f}]') </code></pre>

          <pre
            v-if="calculationData.method === 'rawData'
              && calculationData.answer === 'Yes'"
            class="language-python"
          ><code id="python-r-y">import numpy as np

from scipy.stats import norm

# new data

data = <span class="no-wrap">{{ calculationData.rawDataMeanArrayActualInput }}</span>

# specify the population standard deviation

pop_std = {{ calculationData.standardDeviation }}

# calculate the sample mean

mean = np.mean(data)

# specify the desired level of confidence

alpha = {{ calculationData.levelOfSignificance }}

# find the z-score for the given level of confidence

z = norm.ppf(1 - alpha/2)

# calculate the standard error

se = pop_std / np.sqrt(len(data))

# calculate the lower and upper bounds of the confidence interval

lower = mean - z * se

upper = mean + z * se

print(f'Confidence interval: [{lower:.4f}, {upper:.4f}]') </code></pre>

          <pre
            v-if="calculationData.method === 'readyData'
              && calculationData.answer === 'Yes'"
            class="language-python"
          ><code id="python-re-y">import numpy as np

from scipy.stats import norm

# Known population standard deviation

pop_std = {{ calculationData.standardDeviation }}

# Sample Mean

mean = {{ calculationData.mean }}

# Sample Size

n = {{ calculationData.size }}

# Confidence Level

alpha = {{ calculationData.levelOfSignificance }}

# Calculate z-critical value

z_critical = norm.ppf(1 - alpha / 2)

# Calculate Margin of Error

moe = z_critical * (pop_std / (n ** 0.5))

# Calculate Confidence Interval

ci = [mean - moe, mean + moe]

print(f'Confidence interval: [{ci[0]:.4f}, {ci[1]:.4f}]') </code></pre>
        </div>
      </div>
      <button class="btn-link-accent" :class="{ copied: copyCodeOrCopiedPython === 'Copied' }" @click="copyCode(currentPythonId)">
        {{ copyCodeOrCopiedPython }}
      </button>
    </div>
    <div class="python-r-codes">
      <span class="img-text"><img alt="Icon for R language" src="../../../../assets/images/icon-r-calc.png"> <h4 class="h6">Your Result in R:</h4></span>
      <span class="codes-info">
        The following R code should provide the confidence interval using the input data you entered.
      </span>
      <div class="codes-container">
        <div class="codes">
          <pre
            v-if="calculationData.method === 'rawData'
              && calculationData.answer === 'No'"
            class="language-r"
          ><code id="r-r-n"># dataset

data &lt;- c({{ actualArrayJoin }})

# calculate the sample mean

mean &lt;- mean(data)

# calculate the sample standard deviation

std &lt;- sd(data)

# specify the desired level of confidence

alpha &lt;- {{ calculationData.levelOfSignificance }}

# find the t-score for the given level of confidence

t_score &lt;- qt(1 - alpha/2, df = length(data) - 1)

# calculate the standard error

se &lt;- std / sqrt(length(data))

# calculate the lower and upper bounds of the confidence interval

lower &lt;- mean - t_score * se

upper &lt;- mean + t_score * se

cat("Confidence interval: [", lower, ", ", upper, "]")</code></pre>

          <pre
            v-if="calculationData.method === 'readyData'
              && calculationData.answer === 'No'"
            class="language-r"
          ><code id="r-re-n"># Sample Mean

mean &lt;- {{ calculationData.mean }}

# Sample Standard Deviation

std &lt;- {{ calculationData.standardDeviation }}

# Sample Size

n &lt;- {{ calculationData.size }}

# Confidence Level

alpha &lt;- {{ calculationData.levelOfSignificance }}

# Degrees of Freedom

df &lt;- n - 1

# Calculate t-critical value

t_critical &lt;- qt(1 - alpha/2, df)

# Calculate Margin of Error

moe &lt;- t_critical * (std / sqrt(n))

# Calculate Confidence Interval

ci &lt;- c(mean - moe, mean + moe)

cat("Confidence interval: [", ci[1], ", ", ci[2], "]")</code></pre>

          <pre
            v-if="calculationData.method === 'rawData'
              && calculationData.answer === 'Yes'"
            class="language-r"
          ><code id="r-r-y"># Known population standard deviation

pop_std &lt;- {{ calculationData.standardDeviation }}

# Sample Mean

mean &lt;- mean(c({{ actualArrayJoin }}))

# Sample Size

n &lt;- {{ calculationData.size }}

# Confidence Level

alpha &lt;- {{ calculationData.levelOfSignificance }}

# Calculate z-critical value

z_critical &lt;- qnorm(1 - alpha / 2)

# Calculate Margin of Error

moe &lt;- z_critical * (pop_std / sqrt(n))

# Calculate Confidence Interval

ci &lt;- c(mean - moe, mean + moe)

cat("Confidence interval: [", ci[1], ", ", ci[2], "]") </code></pre>

          <pre
            v-if="calculationData.method === 'readyData'
              && calculationData.answer === 'Yes'"
            class="language-r"
          ><code id="r-re-yes"># Known population standard deviation

pop_std &lt;- {{ calculationData.standardDeviation }}

# Sample Mean

mean &lt;- {{ calculationData.mean }}

# Sample Size

n &lt;- {{ calculationData.size }}

# Confidence Level

alpha &lt;- {{ calculationData.levelOfSignificance }}

# Calculate z-critical value

z_critical &lt;- qnorm(1 - alpha / 2)

# Calculate Margin of Error

moe &lt;- z_critical * (pop_std / sqrt(n))

# Calculate Confidence Interval

ci &lt;- c(mean - moe, mean + moe)

cat("Confidence interval: [", ci[1], ", ", ci[2], "]")</code></pre>
        </div>
      </div>
      <button class="btn-link-accent" :class="{ copied: copyCodeOrCopiedR === 'Copied' }" @click="copyCode(currentRid)">
        {{ copyCodeOrCopiedR }}
      </button>
    </div>
  </div>
</template>

<script>
import calculatorsStepsMixin from '@itbpbg/lms-components/src/mixins/calculators/steps';
import DynamicTables from '../Tables/confidence-interval/DynamicTables.vue';
import { TABLES as tables } from '../../../../utils/calculators';

export default {
  components: {
    DynamicTables,
  },
  mixins: [calculatorsStepsMixin],
  data() {
    return {
      currentPythonId: '',
      currentRid: '',
    };
  },
  computed: {
    getCurrentTableAndScore() {
      return this.calculationData.answer === 'Yes' ? [tables.sndTable.values, 1 - this.calculationData.levelOfSignificance / 2]
        : [tables.stdTable.values, Number(this.calculationData.currentScore)];
    },
    additionString() {
      const { length } = this.calculationData.rawDataMeanArray;
      const rawDataMeanArray = this.calculationData.rawDataMeanArray.map((num, i) => (length - 1 > i ? `${num} + ` : num));
      return rawDataMeanArray.join('');
    },
    sqrString() {
      const { length } = this.calculationData.rawDataMeanArray;
      const rawDataMeanArray = this.calculationData.rawDataMeanArray.map((num, i) => (length - 1 > i ? `(${num} - ${this.calculationData.mean})^2 + ` : `(${num} - ${this.calculationData.mean})^2`));
      return rawDataMeanArray.join('');
    },
    numberContained() {
      if (this.calculationData.answer === 'Yes') {
        for (let i = 0; i < tables.sndTable.values.length; i += 1) {
          for (let y = 0; y < tables.sndTable.values[i].length; y += 1) {
            if (tables.sndTable.values[i][y] === 1 - this.calculationData.levelOfSignificance / 2) {
              return true;
            }
          }
        }
      } else {
        for (let i = 0; i < tables.stdTable.values.length; i += 1) {
          for (let y = 0; y < tables.stdTable.values[i].length; y += 1) {
            if (tables.stdTable.values[i][y] === Number(this.calculationData.currentScore)
            && [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21,
              22, 23, 24, 25, 26, 27, 28, 29, 30, 35, 40, 50, 60, 120].includes(Number(this.calculationData.size - 1))) {
              return true;
            }
          }
        }
      }

      return false;
    },
    actualArrayJoin() {
      return this.calculationData.rawDataMeanArrayActualInput.join(', ');
    },
    meanMinusMof() {
      return (Number(this.calculationData.mean) - Number(this.calculationData.marginOfError)).toFixed(2);
    },
    meanPlusMof() {
      return (Number(this.calculationData.mean) + Number(this.calculationData.marginOfError)).toFixed(2);
    },
    sizeMinusOne() {
      return this.calculationData.size - 1;
    },
    losDividedByTwo() {
      return this.calculationData.levelOfSignificance / 2;
    },
  },
  watch: {
    result() {
      if (this.calculationData.method === 'rawData') {
        if (this.calculationData.answer === 'No') {
          this.currentPythonId = 'python-r-n';
          this.currentRid = 'r-r-n';
        } else {
          this.currentPythonId = 'python-r-y';
          this.currentRid = 'r-r-y';
        }
      } else if (this.calculationData.answer === 'No') {
        this.currentPythonId = 'python-re-n';
        this.currentRid = 'r-re-n';
      } else {
        this.currentPythonId = 'python-re-y';
        this.currentRid = 'r-re-y';
      }
    },
  },
  methods: {
    scrollToSection(e) {
      let yOffset;
      if (this.$store.state.layout === 'desktop') {
        yOffset = -90;
      } else {
        yOffset = this.$config.project === 'lms' ? -64 : -68;
      }

      const section = `${e?.target?.getAttribute('data-section') || e?.target?.closest('li')?.getAttribute('data-section')}`;
      const element = document.querySelector(`#${section}`);
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;

      setTimeout(() => {
        this.clickedSection = section;
        window.scrollTo({ top: y, behavior: 'smooth' });

        setTimeout(() => {
          this.clickedSection = null;
        }, 500);
      }, 200);
    },
  },
};
</script>

<style lang="scss" scoped>
.calculation-steps {
  &.default {
    margin-top: 0;
  }

  p {
    margin: 0;
  }
}

@import '../../../../assets/sass/ds/calculators/calculation-steps.scss';
</style>
